import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ParticipantResponseStatusesEnum} from '../../../models/api/participantModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';

/*
 * Props.
 */

interface CalendarEventParticipantsSummaryProps {
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
}

/*
 * Styles.
 */

const StyledParticipantSummaryDiv = styled.div`
  margin-bottom: 10px;
`;

const StyledParticipantsCountDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const CalendarEventParticipantsSummary: FC<CalendarEventParticipantsSummaryProps> = (props) => {
  // Build responseStatus descriptions like "2 yes", "4 maybe", "8 awaiting".
  const descriptions = _(props.visualParticipants)
    .groupBy('responseStatus')
    .map((participants, responseStatus) => {
      const count = participants.length;
      if (count === 0) {
        return null;
      }

      const descriptor = renderDescriptorForResponseStatus(responseStatus);
      if (!descriptor) {
        return null;
      }

      return {
        responseStatus,
        content: (
          <>
            {`${participants.length} `}
            {descriptor}
          </>
        ),
      };
    })
    .compact()
    .value();

  return (
    <StyledParticipantSummaryDiv>
      {`${props.visualParticipants.length} `}
      <Trans id="participant-response-summary-guests">guests</Trans>
      <StyledParticipantsCountDiv>
        {descriptions.map((description, index) => (
          <span key={`${description.responseStatus}`}>
            {index > 0 && ', '}
            {description.content}
          </span>
        ))}
      </StyledParticipantsCountDiv>
    </StyledParticipantSummaryDiv>
  );
};

function renderDescriptorForResponseStatus(responseStatus: string) {
  switch (responseStatus) {
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return <Trans id="participant-response-status-yes">yes</Trans>;
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return <Trans id="participant-response-status-maybe">maybe</Trans>;
    case ParticipantResponseStatusesEnum.DECLINED:
      return <Trans id="participant-response-status-no">no</Trans>;
    case ParticipantResponseStatusesEnum.PENDING:
      return <Trans id="participant-response-status-awaiting">awaiting</Trans>;
    default:
      return null;
  }
}
